import { supabase } from './supabase'

/**
 * One Question for Communications — 131 (David, 5 Oct). The client half of
 * `#/support/one-question`.
 *
 * Employees never see each other's answers; Communications reads them all,
 * named, and builds One Voice from them (a word cloud, a map, whatever the
 * week calls for), quoting WITHOUT names — the card tells every answerer that
 * before they type. Both reads RAISE for anybody without analytics.read
 * (`app.one_question_reader_tenant()`); the screen renders the refusal's
 * words rather than an empty list (rule 12).
 *
 * The CSV is the hand-off: it is what goes into the artifact. It is built
 * here from rows the server already filtered, so it can never hold more than
 * the screen shows.
 */

export type CommsWeek = {
  contentId: string
  seq: number
  body: string | null
  opensAt: string
  closesAt: string
  status: 'live' | 'ended' | string
  answerCount: number
}

export type CommsAnswer = {
  answerId: string
  authorName: string
  businessUnit: string | null
  department: string | null
  legalEntity: string | null
  locale: string | null
  body: string
  answeredAt: string
  changedAt: string | null
}

export async function loadCommsWeeks(): Promise<CommsWeek[]> {
  const { data, error } = await supabase.rpc('one_question_weeks_for_comms')
  if (error) throw error
  return ((data ?? []) as {
    content_id: string
    seq: number
    body: string | null
    opens_at: string
    closes_at: string
    status: string
    answer_count: number
  }[]).map((r) => ({
    contentId: r.content_id,
    seq: r.seq,
    body: r.body,
    opensAt: r.opens_at,
    closesAt: r.closes_at,
    status: r.status,
    answerCount: r.answer_count,
  }))
}

export async function loadCommsAnswers(contentId: string): Promise<CommsAnswer[]> {
  const { data, error } = await supabase.rpc('one_question_answers_for_comms', { p_content_id: contentId })
  if (error) throw error
  return ((data ?? []) as {
    answer_id: string
    author_name: string
    business_unit: string | null
    department: string | null
    legal_entity: string | null
    author_locale: string | null
    body: string
    answered_at: string
    changed_at: string | null
  }[]).map((r) => ({
    answerId: r.answer_id,
    authorName: r.author_name,
    businessUnit: r.business_unit,
    department: r.department,
    legalEntity: r.legal_entity,
    locale: r.author_locale,
    body: r.body,
    answeredAt: r.answered_at,
    changedAt: r.changed_at,
  }))
}

/**
 * One CSV field. Every field is quoted and inner quotes doubled (RFC 4180),
 * and a field that starts like a formula gets a leading apostrophe: these are
 * words people typed, and the file is opened in Excel.
 */
export function csvField(v: string | number | null | undefined): string {
  let s = v === null || v === undefined ? '' : String(v)
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`
  return `"${s.replace(/"/g, '""')}"`
}

export const CSV_HEADER = [
  'Week',
  'Question',
  'Name',
  'Business unit',
  'Department',
  'Legal entity',
  'Language',
  'Answer',
  'Answered (UTC)',
  'Changed (UTC)',
] as const

/** The whole week as CSV, BOM first so Excel reads the Spanish as UTF-8. */
export function answersCsv(week: CommsWeek, answers: CommsAnswer[]): string {
  const lines = [CSV_HEADER.map(csvField).join(',')]
  for (const a of answers) {
    lines.push(
      [
        week.seq,
        week.body,
        a.authorName,
        a.businessUnit,
        a.department,
        a.legalEntity,
        a.locale,
        a.body,
        a.answeredAt,
        a.changedAt,
      ]
        .map(csvField)
        .join(','),
    )
  }
  return '﻿' + lines.join('\r\n') + '\r\n'
}

/** `one-question-week-01-2026-10-12.csv` — sorts by week in a folder. */
export function csvFileName(week: CommsWeek): string {
  return `one-question-week-${String(week.seq).padStart(2, '0')}-${week.opensAt.slice(0, 10)}.csv`
}

export function downloadText(name: string, text: string, type = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([text], { type }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
